// rejuvenationThreadsData.ts

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[];
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const rejuvenationThreads: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    "en": {
        "url": "",
        "meta": {
            "title": "Rejuvenation Threads - Elite Body Home Dubai",
            "description": "Rejuvenation Threads, also known as thread lifts, are a minimally invasive cosmetic procedure designed to lift and tighten sagging skin, giving you a more youthful and refreshed appearance.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/services/rejuvenation-threads/",
            "og": {
                "locale": "en_US",
                "type": "article",
                "title": "Rejuvenation Threads - Elite Body Home Dubai",
                "description": "Rejuvenation Threads, also known as thread lifts, are a minimally invasive cosmetic procedure designed to lift and tighten sagging skin, giving you a more youthful and refreshed appearance.",
                "url": "https://elitebodyhome.com/services/rejuvenation-threads/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2025-02-13T13:53:50+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/Rejuvenation-Threads-in-dubai-1-1.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/Rejuvenation-Threads-in-dubai-1-1.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "Rejuvenation Threads",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "Rejuvenation Threads - Elite Body Home Dubai",
                "description": "Rejuvenation Threads, also known as thread lifts, are a minimally invasive cosmetic procedure designed to lift and tighten sagging skin, giving you a more youthful and refreshed appearance.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/Rejuvenation-Threads-in-dubai-1-1.jpg"
            },
        },
        "breadcrumbs": ["Home", "Services", "Rejuvenation Threads"],
        "hero": {
            "title": "Rejuvenation Threads",
            "subtitle": "Revive Your Glow, Redefine Your Youth",
            "description": "Rejuvenation Threads, also referred to as thread lifts, are a minimally invasive cosmetic procedure used for the lifting and tightening of sagging skin to create a more youthful and rejuvenated appearance. This procedure is a favorite among those seeking a non-invasive facelift solution."
        },
        "howItWorks": {
            "title": "How Do Rejuvenation Threads Work?",
            "description": "Rejuvenation threads are special dissolvable threads made from biocompatible materials such as polydioxanone (PDO), poly-L-lactic acid (PLA), or polycaprolactone (PCA). These threads are placed beneath the skin with fine needles for a lifting effect. They eventually dissolve but not before prompting the body's natural healing response, which then increases the production of collagen, elastin and hyaluronic acid for skin-tightening results that last."
        },
        "procedureSteps": {
            "heading": "",
            "para": "",
            "steps": [
                {
                    "title": "Consultation",
                    "desc": "To evaluate your skin and determine the areas to be improved, you need to consult with a specialist in detail.",
                    "icon": "fa-solid fa-comment-medical"
                },
                {
                    "title": "Thread Insertion",
                    "desc": "Local anesthesia is used, and fine threads are meticulously introduced with a very thin needle in order to give the targeted areas an upward and firming effect.",
                    "icon": "fa-solid fa-thread"
                },
                {
                    "title": "Adjustment",
                    "desc": "The threads are subsequently tweaked to give an immediate lift. The positioning is meant to shape the face while easing sagging and wrinkles.",
                    "icon": "fa-solid fa-sliders-h"
                },
                {
                    "title": "Recovery",
                    "desc": "Because the process is non-surgical, recovery is also minimal, and the average patient can return to normal daily activity immediately afterward.",
                    "icon": "fa-solid fa-heartbeat"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits",
            "subheading": "Advantages of Rejuvenation Threads",
            "para": "Rejuvenation threads have gained popularity among those seeking to enhance their physical appearance without resorting to surgery, and with good reason.",
            "items": [
                {
                    "title": "Non-surgical",
                    "desc": "No cuts, sutures or general anesthesia needed.",
                    "icon": "fa-solid fa-times-circle"
                },
                {
                    "title": "Minimal Downtime",
                    "desc": "Patients generally go back to their usual activities within a day or two.",
                    "icon": "fa-solid fa-clock"
                },
                {
                    "title": "Immediate Results",
                    "desc": "Lifting and tightening effects are seen immediately after the procedure, with improvements continuing for several months as collagen production ramps up.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "Natural-Looking Outcome",
                    "desc": "The gradual collagen stimulation will make your face look fresh, but still, natural.",
                    "icon": "fa-solid fa-leaf"
                },
                {
                    "title": "Longevity",
                    "desc": "Results may last 12-24 months depending on thread type and individual skin condition.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "Versatile Treatment Areas",
                    "desc": "It could be performed on different regions of the body like the face, neck, jawline, even body.",
                    "icon": "fa-solid fa-map-marked-alt"
                }
            ]
        },
        "eligibility": {
            "heading": "Eligibility",
            "subheading": "",
            "para": "Rejuvenation Threads can benefit a lot of adults, but there are some criteria that would make you the perfect candidate for this procedure.",
            "items": [
                {
                    "title": "Mild to Moderate Skin Laxity",
                    "desc": "Best candidates are those who have early signs of aging like sagging skin or jowls.",
                    "icon": "fa-solid fa-check-circle"
                },
                {
                    "title": "Healthy Skin",
                    "desc": "For best results, you want to have relatively healthy skin without extreme damage or heavy sagging.",
                    "icon": "fa-solid fa-heart"
                },
                {
                    "title": "Age Group",
                    "desc": "Thread lifts are most commonly used on patients aged 30 to 60.",
                    "icon": "fa-solid fa-user-clock"
                },
                {
                    "title": "No Allergic Reactions",
                    "desc": "Candidates should not be allergic to any of the materials used in the threads.",
                    "icon": "fa-solid fa-allergies"
                }
            ]
        },
        "preparationTips": {
            "heading": "Preparation",
            "subheading": "",
            "para": "The procedure of rejuvenation threads requires proper preparation to make this process as successful and efficient as possible.",
            "items": [
                {
                    "title": "Consultation",
                    "desc": "Undergo comprehensive consultation where your medical history, your goals as well as the risks are discussed in detail.",
                    "icon": "fa-solid fa-comment-dots"
                },
                {
                    "title": "Avoid Anti-Inflammatories",
                    "desc": "Avoid anti-inflammatory medications such as ibuprofen or aspirin for at least one week before the procedure because they can promote bruising.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "Stop Smoking",
                    "desc": "If you smoke, you will have to quit at least two weeks before and after the treatment to aid the healing process.",
                    "icon": "fa-solid fa-smoking-ban"
                },
                {
                    "title": "Skin Care",
                    "desc": "In the days leading up to the procedure, avoid aggressive exfoliating treatments or peels to minimize irritation.",
                    "icon": "fa-solid fa-spa"
                }
            ]
        },
        "results": {
            "heading": "Results",
            "subheading": "",
            "para": "Rejuvenation threads provide noticeably visible results immediately, with heightened effects developing in the weeks and months that follow as the body begins to produce collagen. Your skin should appear smoother, tighter, and lifted and naturally, more youthful.",
            "items": [
                {
                    "title": "Immediate Lifting",
                    "desc": "Immediate visible tightening of skin laxity post surgery",
                    "icon": "fa-solid fa-arrow-up"
                },
                {
                    "title": "Progressive Enhancement",
                    "desc": "If you smoke, you'll have to quit two weeks before and after the treatment as well, to aid in the healing process.",
                    "icon": "fa-solid fa-chart-line"
                },
                {
                    "title": "Long-Lasting Results",
                    "desc": "Results from rejuvenation threads can last from 12 to 24 months, depending on the material used and individual lifestyle factors.",
                    "icon": "fa-solid fa-hourglass-end"
                }
            ]
        },
        "aftercareTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "How long do rejuvenation threads last?",
                    "answer": "Results can last between 12 to 24 months depending on the type of threads used and individual factors like skin health and age."
                },
                {
                    "question": "Are there any side effects?",
                    "answer": "Common side effects include mild bruising, swelling, or discomfort, which usually subside within a week. Rare complications like infection or thread breakage are minimal when performed by a skilled practitioner."
                },
                {
                    "question": "Is the procedure painful?",
                    "answer": "Local anesthesia ensures that the treatment is virtually pain-free, with only minor discomfort felt during the insertion process."
                },
                {
                    "question": "What areas can be treated with rejuvenation threads?",
                    "answer": "Rejuvenation threads are versatile and can be used on the cheeks, jawline, neck, brow, and even areas of the body such as the arms or knees."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    },
    "shared": {
        "images": {
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Rejuvenation-Threads-in-dubai-2.jpg",
            "beforeAfter": [
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/Rejuvenation-Threads-in-dubai-1.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/Rejuvenation-Threads-in-dubai-2.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/Rejuvenation-Threads-in-dubai-3.jpg"
            ],
            "introductionImage": ""
        },
        "product": {
            "id": 157,
            "title": "Rejuvenation Threads",
            "price": 350,
            "description": "Minimally invasive thread lift procedure that lifts and rejuvenates sagging skin for a natural, youthful appearance."
        }
    },
    "ar": {
    "url": "",
    "meta": {
        "title": "خيوط تجديد شباب البشرة - Elite Body Home دبي",
        "description": "خيوط تجديد شباب البشرة، المعروفة أيضًا بخيوط شد الوجه، هي إجراء تجميلي بسيط مصمم لرفع وشد الجلد المترهل، مما يمنحك مظهرًا أكثر شبابًا وانتعاشًا.",
        "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
        "canonical": "https://elitebodyhome.com/services/rejuvenation-threads/",
        "og": {
            "locale": "ar_AR",
            "type": "article",
            "title": "خيوط تجديد شباب البشرة - Elite Body Home دبي",
            "description": "خيوط تجديد شباب البشرة، المعروفة أيضًا بخيوط شد الوجه، هي إجراء تجميلي بسيط مصمم لرفع وشد الجلد المترهل، مما يمنحك مظهرًا أكثر شبابًا وانتعاشًا.",
            "url": "https://elitebodyhome.com/services/rejuvenation-threads/",
            "site_name": "elitebodyhome.com",
            "updated_time": "2025-02-13T13:53:50+00:00",
            "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/Rejuvenation-Threads-in-dubai-1-1.jpg",
            "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/Rejuvenation-Threads-in-dubai-1-1.jpg",
            "image_width": 1254,
            "image_height": 700,
            "image_alt": "خيوط تجديد شباب البشرة",
            "image_type": "image/jpeg"
        },
        "twitter": {
            "card": "summary_large_image",
            "title": "خيوط تجديد شباب البشرة - Elite Body Home دبي",
            "description": "خيوط تجديد شباب البشرة، المعروفة أيضًا بخيوط شد الوجه، هي إجراء تجميلي بسيط مصمم لرفع وشد الجلد المترهل، مما يمنحك مظهرًا أكثر شبابًا وانتعاشًا.",
            "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/Rejuvenation-Threads-in-dubai-1-1.jpg"
        }
    },
    "breadcrumbs": ["الرئيسية", "الخدمات", "خيوط تجديد شباب البشرة"],
    "hero": {
        "title": "خيوط تجديد شباب البشرة",
        "subtitle": "استعد إشراقتك، وأعد تعريف شبابك",
        "description": "تُعرف خيوط تجديد شباب البشرة باسم خيوط شد الوجه أيضًا، وهي إجراء تجميلي بسيط وغير جراحي. يُستخدم لشد ورفع الجلد المترهل، بهدف استعادة مظهر أكثر شبابًا وحيوية. تُعد خيارًا مثاليًا للأشخاص الذين يبحثون عن بديل غير جراحي لعمليات شد الوجه الجراحية."
    },
    "howItWorks": {
        "title": "كيف تعمل خيوط تجديد شباب البشرة؟",
        "description": "تكون خيوط تجديد البشرة قابلة للذوبان، وتُصنع من مواد متوافقة حيويًا مع الجسم مثل مادة البوليديوكسانون، أو حمض البولي لاكتيك، أو مادة البولي كابرولاكتون. يتم إدخال هذه الخيوط تحت الجلد باستخدام إبر دقيقة بهدف رفع البشرة. تُحفز الخيوط استجابة الجسم الطبيعية للشفاء أثناء ذوبانها تدريجيًا، مما يعزز إنتاج الكولاجين، والإيلاستين، وحمض الهيالورونيك، ويؤدي إلى نتائج شد للجلد تدوم طويلاً."
    },
    "procedureSteps": {
        "heading": "",
        "para": "",
        "steps": [
            {
                "title": "الاستشارة",
                "desc": "تحتاج لإجراء جلسة استشارية مع طبيب التجميل، لتقييم حالة بشرتك وتحديد المناطق التي تحتاج إلى تحسين.",
                "icon": "fa-solid fa-comment-medical"
            },
            {
                "title": "إدخال الخيوط",
                "desc": "يُستخدم مخدر موضعي، ثم تُدخل الخيوط الدقيقة بعناية باستخدام إبرة رفيعة جداً، لتمنح المناطق المستهدفة تأثير الرفع والثبات.",
                "icon": "fa-solid fa-thread"
            },
            {
                "title": "ضبط الخيوط",
                "desc": "بعد تثبيت الخيوط، يتم تعديلها بعناية لتحقيق رفع فوري للجلد. ويهدف هذا إلى نحت ملامح الوجه والتقليل من الترهلات والتجاعيد.",
                "icon": "fa-solid fa-sliders-h"
            },
            {
                "title": "التعافي",
                "desc": "يتطلب فترة تعافي قصيرة نظرًا لكونه إجراء غير جراحي، ويمكن لمعظم المرضى العودة إلى أنشطتهم اليومية مباشرةً بعد الجلسة.",
                "icon": "fa-solid fa-heartbeat"
            }
        ]
    },
    "benefits": {
        "heading": "الفوائد",
        "subheading": "مزايا خيوط تجديد شباب البشرة",
        "para": "اكتسبت خيوط التجديد شهرة واسعة بين الأشخاص الذين يبحثون عن تحسين مظهرهم دون اللجوء إلى الإجراءات الجراحية. وذلك بفضل المزايا التالية:",
        "items": [
            {
                "title": "إجراء غير جراحي",
                "desc": "لا يتطلب تدخل جراحي، أو غرز، أو تخدير عام.",
                "icon": "fa-solid fa-times-circle"
            },
            {
                "title": "لا يتطلب فترة تعافي طويلة",
                "desc": "يمكن للمرضى العودة إلى أنشطتهم اليومية في غضون يوم أو يومين فقط.",
                "icon": "fa-solid fa-clock"
            },
            {
                "title": "نتائج فورية",
                "desc": "تظهر نتائج العلاج من رفع وشد للجلد مباشرةً بعد الإجراء، مع استمرار التحسن لعدة أشهر بفضل زيادة إنتاج الكولاجين.",
                "icon": "fa-solid fa-bolt"
            },
            {
                "title": "نتائج طبيعية",
                "desc": "يمنح التحفيز التدريجي للكولاجين وجهك مظهرًا منتعشًا دون أن يبدو مصطنعًا.",
                "icon": "fa-solid fa-leaf"
            },
            {
                "title": "العمر الافتراضي",
                "desc": "قد تستمر النتائج من 12 إلى 24 شهرًا، وذلك حسب نوع الخيوط وحالة البشرة الفردية.",
                "icon": "fa-solid fa-calendar-check"
            },
            {
                "title": "يلائم استخدامه لعدة مناطق في الجسم",
                "desc": "يمكن استخدامها على مناطق مختلفة من الجسم مثل الوجه، والرقبة، وخط الفك، وبعض مناطق الجسم الأخرى.",
                "icon": "fa-solid fa-map-marked-alt"
            }
        ]
    },
    "eligibility": {
        "heading": "الأهلية",
        "subheading": "",
        "para": "يمكن لخيوط التجديد أن تفيد الكثير من البالغين، إلا أنه توجد بعض المعايير التي قد تجعلك مرشحًا مثاليًا لهذا الإجراء.",
        "items": [
            {
                "title": "إذا كنت تعاني من ترهل خفيف أو متوسط في الجلد",
                "desc": "يحقق الإجراء أفضل نتائج لمن يعاني من علامات الشيخوخة المبكرة، مثل ترهل الجلد أو بداية ظهور اللغد.",
                "icon": "fa-solid fa-check-circle"
            },
            {
                "title": "إذا كنت تمتلك بشرة صحية",
                "desc": "يُفضل أن تكون البشرة بصحة جيدة نسبيًا دون تلف شديد أو ترهل كبير للحصول على أفضل نتائج.",
                "icon": "fa-solid fa-heart"
            },
            {
                "title": "إذا كنت ضمن الفئة العمرية المناسبة",
                "desc": "يُجرى شد الخيوط عادةً للأشخاص الذين تتراوح أعمارهم بين 30 و60 عامًا.",
                "icon": "fa-solid fa-user-clock"
            },
            {
                "title": "إذا كنت لا تعاني من ردود فعل تحسسية",
                "desc": "يجب ألا يكون لدى المريض أي رد فعل تحسسي تجاه المواد المستخدمة في الخيوط.",
                "icon": "fa-solid fa-allergies"
            }
        ]
    },
    "preparationTips": {
        "heading": "الاستعداد",
        "subheading": "",
        "para": "يتطلب إجراء خيوط تجديد شباب البشرة التحضير الجيد، لضمان نجاح هذا الإجراء وتحقيق أفضل النتائج بكفاءة عالية.",
        "items": [
            {
                "title": "إجراء جلسة استشارية",
                "desc": "اخضع لجلسة استشارية شاملة، يتم خلالها مناقشة تاريخك الطبي، وأهدافك، والمخاطر المحتملة بالتفصيل.",
                "icon": "fa-solid fa-comment-dots"
            },
            {
                "title": "تجنب الأدوية المضادة للالتهاب",
                "desc": "امتنع عن استخدام الأدوية مثل الإيبوبروفين أو الأسبرين لمدة لا تقل عن أسبوع قبل الجلسة، لأنها قد ترفع احتمالية ظهور كدمات.",
                "icon": "fa-solid fa-pills"
            },
            {
                "title": "الامتناع عن التدخين",
                "desc": "إذا كنت مدخنًا، يجب التوقف عن التدخين لمدة أسبوعين قبل الجلسة وأسبوعين بعد الجلسة، لأن التدخين يبطئ عملية التعافي.",
                "icon": "fa-solid fa-smoking-ban"
            },
            {
                "title": "العناية بالبشرة",
                "desc": "تجنب جلسات التقشير والمقشرات القوية خلال الأيام التي تسبق الإجراء، لتقليل التهاب البشرة.",
                "icon": "fa-solid fa-spa"
            }
        ]
    },
    "results": {
        "heading": "النتائج",
        "subheading": "",
        "para": "توفر خيوط التجديد نتائج مرئية بشكل فوري، مع تطور النتائج بشكل متزايد خلال الأسابيع والأشهر التالية للجلسة مع بدء الجسم في إنتاج الكولاجين. سيظهر الجلد مشدودًا ومرفوعًا وأكثر نعومة، ويمنح مظهرًا أكثر شبابًا بشكل طبيعي.",
        "items": [
            {
                "title": "رفع فوري للجلد",
                "desc": "يُلاحظ شد واضح لترهلات الجلد فورًا بعد الإجراء.",
                "icon": "fa-solid fa-arrow-up"
            },
            {
                "title": "تحسن تدريجي",
                "desc": "إذا كنت مدخنًا، يجب التوقف عن التدخين لمدة أسبوعين قبل الجلسة وأسبوعين بعد الجلسة، لأن التدخين يبطئ عملية التعافي.",
                "icon": "fa-solid fa-chart-line"
            },
            {
                "title": "نتائج تدوم طويلًا",
                "desc": "تدوم نتائج خيوط التجديد ما بين 12 إلى 24 شهرًا، حسب نوع الخيوط المستخدمة والعوامل المرتبطة بنمط الحياة الفردي.",
                "icon": "fa-solid fa-hourglass-end"
            }
        ]
    },
    "aftercareTips": {
        "heading": "",
        "subheading": "",
        "para": "",
        "items": []
    },
    "faqData": {
        "heading": "الأسئلة الشائعة",
        "subheading": "",
        "items": [
            {
                "question": "كم تدوم نتائج خيوط تجديد شباب البشرة؟",
                "answer": "قد تدوم النتائج ما بين 12 إلى 24 شهرًا حسب نوع الخيوط المستخدمة والعوامل الفردية مثل صحة البشرة والعمر."
            },
            {
                "question": "هل هناك أي آثار جانبية؟",
                "answer": "تشمل الآثار الجانبية الشائعة كدمات خفيفة، تورم، أو انزعاج، والتي تختفي عادةً خلال أسبوع. المضاعفات النادرة مثل العدوى أو كسر الخيوط تكون ضئيلة عند إجرائها بواسطة أخصائي متمرس."
            },
            {
                "question": "هل الإجراء مؤلم؟",
                "answer": "يضمن التخدير الموضعي أن العلاج غير مؤلم عمليًا، مع شعور بانزعاج بسيط أثناء عملية إدخال الخيوط."
            },
            {
                "question": "ما هي المناطق التي يمكن علاجها بخيوط التجديد؟",
                "answer": "خيوط التجديد متعددة الاستخدامات ويمكن تطبيقها على الخدين، خط الفك، الرقبة، الحاجبين، وحتى بعض مناطق الجسم مثل الذراعين أو الركبتين."
            }
        ]
    },
    "treatmentAreas1": [],
    "treatmentAreas2": []
}
};
